import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Building2, Globe, Palette, ShieldCheck, Upload } from "lucide-react";
import client from "@/api";
import { useTenant } from "@/context/TenantContext";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const init = { agency_name: "", owner_name: "", email: "", phone: "", password: "", subdomain: "", primary_color: "#1B1F5B", secondary_color: "#F5B32B", pan: "", gst: "" };

const useSubCheck = (sub) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!sub || sub.length < 3) { setState(null); return; }
    setState({ loading: true });
    const t = setTimeout(() => client.get("/agency/subdomain-check", { params: { sub } })
      .then((r) => setState(r.data)).catch(() => setState(null)), 400);
    return () => clearTimeout(t);
  }, [sub]);
  return state;
};

const Label = ({ children }) => <span className="mb-1.5 block text-[13px] font-semibold text-slate-700">{children}</span>;

const SubStatus = ({ s }) => {
  if (!s) return null;
  if (s.loading) return <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Checking…</p>;
  return (
    <p className={`mt-1.5 flex items-center gap-1 text-xs ${s.available ? "text-[#0E7C6B]" : "text-red-600"}`} data-testid="signup-subdomain-status">
      {s.available ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {s.available ? `${s.url} is available` : s.reason}
    </p>
  );
};

const Perks = () => (
  <div className="space-y-6 text-white">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5B32B]">FLYTRIP for agencies</p>
    <h1 className="text-4xl font-extrabold leading-tight lg:text-5xl">Your own travel website, live in a day.</h1>
    <p className="max-w-md text-white/70">Sell live flights, hotels and holidays under your brand. We run the tech, inventory and payments — you keep the margin.</p>
    {[[Globe, "Your subdomain or custom domain"], [Palette, "Your logo, colours and contact details everywhere"], [ShieldCheck, "Set your own markup — earnings tracked automatically"]].map(([I, t]) => (
      <div key={t} className="flex items-center gap-3"><span className="rounded-lg bg-white/10 p-2"><I className="h-5 w-5 text-[#F5B32B]" /></span><span className="text-sm">{t}</span></div>
    ))}
  </div>
);

const Done = ({ f, root }) => (
  <div className="py-10 text-center" data-testid="signup-success">
    <CheckCircle2 className="mx-auto h-14 w-14 text-[#0E7C6B]" />
    <h2 className="mt-4 text-2xl font-bold text-[#1B1F5B]">Application received</h2>
    <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
      We're reviewing <b>{f.agency_name}</b>. Once approved, <b>{f.subdomain}.{root}</b> goes live and you can log in to the Agency Admin with {f.email}.
    </p>
    <Link to="/agency-admin"><Button className="mt-6 bg-[#1B1F5B] hover:bg-[#141844]" data-testid="signup-go-admin-btn">Go to Agency Admin</Button></Link>
  </div>
);

export default function AgencySignup() {
  const [f, setF] = useState(init);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const root = useTenant().rootDomain || "ajanabeetravels.in";
  const sub = useSubCheck(f.subdomain);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sub && !sub.loading && !sub.available) return toast.error(`Subdomain: ${sub.reason}`);
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (logo) fd.append("logo", logo);
    try {
      await client.post("/agency/signup", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (x) {
      const d = x?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the form");
    } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <label className="block"><Label>{label}</Label><Input value={f[k]} onChange={set(k)} required data-testid={`signup-${k.replace("_", "-")}-input`} {...props} /></label>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />
      <section className="bg-[#1B1F5B]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 lg:grid-cols-[1fr_560px] lg:py-20">
          <Perks />
          <div className="rounded-2xl bg-white p-6 shadow-2xl md:p-8" data-testid="agency-signup-card">
            {done ? <Done f={f} root={root} /> : (
              <form onSubmit={submit} className="space-y-4" data-testid="agency-signup-form">
                <div className="flex items-center gap-2"><Building2 className="h-6 w-6 text-[#1B1F5B]" /><h2 className="text-xl font-bold text-[#1B1F5B]">Start your agency</h2></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {field("agency_name", "Agency name")}
                  {field("owner_name", "Owner name")}
                  {field("email", "Email", { type: "email" })}
                  {field("phone", "Mobile", { inputMode: "numeric", maxLength: 14 })}
                </div>
                {field("password", "Password (min 8 characters)", { type: "password", minLength: 8 })}
                <label className="block">
                  <Label>Choose your subdomain</Label>
                  <div className="flex items-center overflow-hidden rounded-md border focus-within:ring-2 focus-within:ring-[#1B1F5B]/30">
                    <input value={f.subdomain} required data-testid="signup-subdomain-input"
                      onChange={(e) => setF({ ...f, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                      className="h-10 flex-1 px-3 text-sm outline-none" placeholder="youragency" />
                    <span className="bg-slate-50 px-3 py-2.5 text-sm text-slate-500" data-testid="signup-root-domain">.{root}</span>
                  </div>
                  <SubStatus s={sub} />
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  {field("pan", "PAN", { maxLength: 10, onChange: (e) => setF({ ...f, pan: e.target.value.toUpperCase() }) })}
                  {field("gst", "GSTIN (optional)", { required: false, maxLength: 15, onChange: (e) => setF({ ...f, gst: e.target.value.toUpperCase() }) })}
                </div>
                <div className="flex flex-wrap items-end gap-4">
                  {[["primary_color", "Primary"], ["secondary_color", "Accent"]].map(([k, l]) => (
                    <label key={k} className="block"><Label>{l} colour</Label>
                      <input type="color" value={f[k]} onChange={set(k)} className="h-10 w-16 cursor-pointer rounded border" data-testid={`signup-${k.replace("_", "-")}-input`} /></label>
                  ))}
                  <div>
                    <Label>Logo (optional)</Label>
                    <input ref={fileRef} type="file" hidden accept=".png,.jpg,.jpeg,.webp,.svg" onChange={(e) => setLogo(e.target.files?.[0] || null)} data-testid="signup-logo-file" />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="signup-logo-btn">
                      <Upload className="mr-1.5 h-4 w-4" />{logo ? logo.name.slice(0, 18) : "Upload logo"}
                    </Button>
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="h-11 w-full bg-[#F5B32B] text-base font-bold text-[#1B1F5B] hover:bg-[#E89A10]" data-testid="signup-submit-btn">
                  {busy ? "Submitting…" : "Submit application"}
                </Button>
                <p className="text-center text-xs text-slate-500">Already approved? <Link to="/agency-admin" className="font-semibold text-[#1B1F5B]">Agency login</Link></p>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
